/**
 * BOPly Analytics - Custom Event Tracking
 * 
 * This file adds custom events to Firebase Analytics.
 * Events will appear in Firebase Console → Analytics → Events
 * 
 * NOTE: New events take 24-48 hours to appear the first time.
 * Use DebugView for real-time testing.
 */

import { getAnalytics, logEvent } from 'firebase/analytics';
import firebaseApp from '../firebase';

// Initialize analytics
let analytics = null;

export const initAnalytics = () => {
  try {
    analytics = getAnalytics(firebaseApp);
    console.log('✅ BOPly Analytics initialized');
    return analytics;
  } catch (error) {
    console.warn('Analytics initialization failed:', error);
    return null;
  }
};

// ============================================
// TRACKING FUNCTIONS
// ============================================

/**
 * Track any custom event
 */
export const trackEvent = (eventName, params = {}) => {
  if (!analytics) {
    // Try to initialize if not already done
    try {
      analytics = getAnalytics(firebaseApp);
    } catch (e) {
      console.warn('Analytics not available');
      return;
    }
  }
  
  try {
    logEvent(analytics, eventName, {
      ...params,
      timestamp: new Date().toISOString(),
    });
    
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      // console.log(`📊 Event: ${eventName}`, params);
    }
  } catch (error) {
    console.error('Failed to track event:', error);
  }
};

/**
 * Track screen/page views
 */
export const trackScreenView = (screenName) => {
  trackEvent('screen_view', {
    screen_name: screenName,
  });
};

// ============================================
// ONBOARDING EVENTS (Updated for Demo Mode)
// ============================================

export const trackOnboarding = {
  // Welcome modal shown to new user
  welcomeShown: () => {
    trackEvent('welcome_modal_shown');
  },

  // Track what choice user made on welcome modal
  welcomeModalChoice: (choice) => {
    trackEvent('welcome_modal_choice', { 
      choice: choice // 'demo', 'start_fresh', 'dismissed'
    });
  },

  // User started demo mode
  demoStarted: () => {
    trackEvent('demo_mode_started');
  },

  // User chose "Start Fresh" instead of demo
  startFreshChosen: () => {
    trackEvent('onboarding_start_fresh');
  },

  // User clicked "Create My Band" from demo mode
  createBandClicked: (source = 'demo_banner') => {
    trackEvent('demo_create_band_clicked', { source });
  },

  // Original onboarding started (kept for backwards compatibility)
  started: () => {
    trackEvent('onboarding_started');
  },
  
  stepViewed: (step) => {
    trackEvent('onboarding_step_viewed', { step });
  },

  // User started filling out the add show form
  addShowFormStarted: () => {
    trackEvent('add_show_form_started');
  },

  // User abandoned the form without completing
  addShowFormAbandoned: (fieldsCompleted) => {
    trackEvent('add_show_form_abandoned', {
      fields_completed: fieldsCompleted.join(','), // e.g., 'venue,date'
    });
  },
  
  // Onboarding completed - user created their band
  completed: (firstShowAdded = false) => {
    trackEvent('onboarding_completed', {
      first_show_added: firstShowAdded,
    });
  },
  
  skipped: (atStep) => {
    trackEvent('onboarding_skipped', { 
      skipped_at_step: atStep 
    });
  },
  
  // First show added during onboarding
  firstShowAdded: (showData) => {
    trackEvent('first_show_added', {
      has_venue: !!showData?.venue,
      has_city: !!showData?.city,
      has_guarantee: !!showData?.guarantee,
      show_type: showData?.showType || 'unknown',
      source: showData?.source || 'onboarding',
    });
  },

  // Band created (new)
  bandCreated: (bandName, fromDemo = false) => {
    trackEvent('band_created', {
      from_demo: fromDemo,
      has_custom_name: bandName && bandName.toLowerCase() !== 'my band',
    });
  },

  // Guided tour events
  tourStarted: () => {
    trackEvent('guided_tour_started');
  },

  tourCompleted: () => {
    trackEvent('guided_tour_completed');
  },

  tourSkipped: (atStep) => {
    trackEvent('guided_tour_skipped', { skipped_at_step: atStep });
  },
};

// ============================================
// SHOW EVENTS
// ============================================

export const trackShows = {
  add: (showData, showCount = 1) => {
    trackEvent('add_show', {
      has_guarantee: !!showData?.guarantee,
      has_city: !!showData?.city,
      event_type: showData?.eventType || 'show',
      show_count: showCount, // Total shows after adding this one
    });

    // Track second show added - this is a key retention signal!
    if (showCount === 2) {
      trackEvent('second_show_added', {
        days_since_first: showData?.daysSinceFirst || null,
        source: showData?.source || 'app',
      });
    }
  },

  // Standalone second show tracker (alternative method)
  secondShowAdded: (daysSinceFirst = null) => {
    trackEvent('second_show_added', {
      days_since_first: daysSinceFirst,
    });
  },
  
  edit: () => {
    trackEvent('edit_show');
  },
  
  delete: () => {
    trackEvent('delete_show');
  },

  // Track show milestones
  milestone: (count) => {
    // Track at 5, 10, 25, 50, 100 shows
    if ([5, 10, 25, 50, 100].includes(count)) {
      trackEvent('show_milestone', { count });
    }
  },
};

// ============================================
// INVENTORY EVENTS
// ============================================

export const trackInventory = {
  addItem: () => {
    trackEvent('add_merch_item');
  },
  
  editItem: () => {
    trackEvent('edit_merch_item');
  },
  
  deleteItem: () => {
    trackEvent('delete_merch_item');
  },
  
  recordSale: (quantity, total) => {
    trackEvent('record_merch_sale', {
      quantity,
      revenue_range: getAmountRange(total),
    });
  },
};

// ============================================
// CONTACT EVENTS
// ============================================

export const trackContacts = {
  add: (hasTags = false, tagCount = 0) => {
    trackEvent('add_contact', {
      has_tags: hasTags,
      tag_count: tagCount,
    });
  },

  edit: () => {
    trackEvent('edit_contact');
  },

  delete: () => {
    trackEvent('delete_contact');
  },

  import: (source = 'show') => {
    trackEvent('import_contact', { source });
  },

  filterByTag: (tagId) => {
    trackEvent('filter_contacts_by_tag', { tag: tagId });
  },
};

// ============================================
// POLL EVENTS
// ============================================

export const trackPolls = {
  create: (optionCount = 2) => {
    trackEvent('create_poll', { option_count: optionCount });
  },

  vote: () => {
    trackEvent('vote_poll');
  },

  close: () => {
    trackEvent('close_poll');
  },

  delete: () => {
    trackEvent('delete_poll');
  },
};

// ============================================
// FINANCE EVENTS
// ============================================

export const trackFinances = {
  addExpense: (category) => {
    trackEvent('add_expense', { category });
  },
  
  viewSummary: () => {
    trackEvent('view_financial_summary');
  },
  
  exportData: (type) => {
    trackEvent('export_data', { export_type: type });
  },
};

// ============================================
// ENGAGEMENT EVENTS
// ============================================

export const trackEngagement = {
  openSettings: () => {
    trackEvent('open_settings');
  },
  
  inviteMember: (method = 'code') => {
    trackEvent('invite_member', { method }); // 'code', 'email', 'link'
  },
  
  switchGroup: () => {
    trackEvent('switch_group');
  },
  
  sendFeedback: () => {
    trackEvent('send_feedback');
  },
  
  toggleDarkMode: (isDark) => {
    trackEvent('toggle_dark_mode', { is_dark: isDark });
  },

  // Session tracking
  sessionStart: () => {
    trackEvent('session_start');
  },

  // Feature discovery - track first time using features
  featureDiscovered: (feature) => {
    trackEvent('feature_discovered', { feature });
  },

  // Return visit tracking
  returnVisit: (daysSinceLastVisit) => {
    trackEvent('return_visit', { 
      days_since_last: daysSinceLastVisit,
      is_weekly_active: daysSinceLastVisit <= 7,
    });
  },
};

// ============================================
// HELPER FUNCTIONS
// ============================================

// Bucket amounts for privacy (don't send exact values)
const getAmountRange = (amount) => {
  if (!amount || amount <= 0) return 'none';
  if (amount < 50) return 'under_50';
  if (amount < 100) return '50_100';
  if (amount < 250) return '100_250';
  if (amount < 500) return '250_500';
  if (amount < 1000) return '500_1000';
  return 'over_1000';
};

// Screen name constants for consistency
export const SCREENS = {
  DASHBOARD: 'dashboard',
  CALENDAR: 'calendar',
  TIMELINE: 'timeline',
  INVENTORY: 'inventory',
  EXPENSES: 'expenses',
  SUMMARY: 'summary',
  CONTACTS: 'contacts',
  POLLS: 'polls',
  DOCUMENTS: 'documents',
  SETTINGS: 'settings',
  ONBOARDING: 'onboarding',
  WELCOME: 'welcome_modal',
  DEMO_MODE: 'demo_mode',
};

export default {
  initAnalytics,
  trackEvent,
  trackScreenView,
  trackOnboarding,
  trackShows,
  trackInventory,
  trackContacts,
  trackPolls,
  trackFinances,
  trackEngagement,
  SCREENS,
};